import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shows one of the Udyog artwork screens exactly as designed and lets us drop
 * live controls on top of it.
 *
 * The artwork is 720x1280. The stage keeps that shape, fits inside the screen
 * without cropping or stretching, and becomes a CSS container — so children
 * can be positioned in % and sized in `cqw`, and every element lands on the
 * same pixel of the artwork on a phone, a tablet or a laptop.
 *
 * ONE PIECE: the artwork is a real <img>, and the controls are only mounted
 * once that <img> has fired `load`. A preloaded CSS background still had to be
 * fetched a second time by the background layer, which let the button paint
 * alone for a moment on a phone.
 *
 * KEYBOARD: the artwork keeps its full size and slides up so the field being
 * typed into sits near the top of the space the keyboard leaves — the same
 * behaviour as a normal scrolling page. Moving to the next field re-adjusts.
 */
const FIELD = "input,textarea,select";

export const DesignStage = ({ bg, children, testid }) => {
  const [artReady, setArtReady] = useState(false);
  const [baseH, setBaseH] = useState(() =>
    typeof window === "undefined" ? 0 : window.visualViewport?.height || window.innerHeight
  );
  const [shift, setShift] = useState(0);
  const artRef = useRef(null);
  const baseRef = useRef(baseH);
  const shiftRef = useRef(0);
  baseRef.current = baseH;
  shiftRef.current = shift;

  const onImg = useCallback((el) => {
    artRef.current = el;
    if (el?.complete && el.naturalWidth > 0) setArtReady(true);
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    const visible = () => vv?.height || window.innerHeight;

    // One stable position for the whole form: lift it until the LAST field
    // clears the keyboard, but never so far that the FIRST field slides under
    // the browser's top bar.
    // Put the FOCUSED field near the top of whatever space the keyboard has
    // left — the behaviour of a normal scrolling page (Google Forms etc.) —
    // so the student sees what they are typing with room to spare, and the
    // next field rises the same way.
    const place = (h) => {
      const art = artRef.current?.parentElement;
      const el = document.activeElement;
      if (!art || !el?.matches?.(FIELD) || !art.contains(el)) {
        setShift(0);
        return;
      }
      // Geometry is derived from the UNTRANSLATED stage position (the outer
      // box centres it). Measuring the translated rect and adding the current
      // shift back drifts a few pixels on every focus change, which drags the
      // form out from under the student's finger.
      const outer = art.parentElement.getBoundingClientRect();
      const artH = art.offsetHeight;
      const artTop = outer.top + Math.round((outer.height - artH) / 2);
      const relTop = el.getBoundingClientRect().top - art.getBoundingClientRect().top;
      const top = (vv?.offsetTop || 0) + Math.max(48, Math.round(h * 0.14));
      const needed = Math.round(artTop + relTop - top);
      // Don't drag the artwork off the bottom of the visible area.
      const maxUp = Math.max(0, Math.round(artTop + artH - ((vv?.offsetTop || 0) + h)));
      setShift(Math.max(0, Math.min(needed, maxUp)));
    };

    const onViewport = () => {
      const h = visible();
      // A keyboard eats a big chunk; anything smaller is browser chrome
      // settling (the address bar hiding) and becomes the new base height.
      if (h >= baseRef.current - 90) {
        setBaseH(h);
        setShift(0);
        return;
      }
      place(h);
    };

    const onFocus = (e) => {
      if (!e.target?.matches?.(FIELD)) return;
      // The keyboard animates in — measure once it has landed.
      setTimeout(() => {
        const h = visible();
        if (h < baseRef.current - 90) place(h);
      }, 180);
    };
    const onBlur = () => {
      setTimeout(() => {
        if (!document.activeElement?.matches?.(FIELD)) setShift(0);
      }, 150);
    };

    vv?.addEventListener("resize", onViewport);
    vv?.addEventListener("scroll", onViewport);
    window.addEventListener("orientationchange", onViewport);
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    return () => {
      vv?.removeEventListener("resize", onViewport);
      vv?.removeEventListener("scroll", onViewport);
      window.removeEventListener("orientationchange", onViewport);
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
    };
  }, []);

  const h = baseH || 0;

  return (
    <div
      className="fixed inset-0 bg-[#00110a] flex items-center justify-center overflow-hidden"
      style={{ height: h ? `${Math.round(h)}px` : undefined }}
      data-testid={testid}
    >
      {!artReady && (
        <span
          className="absolute w-8 h-8 rounded-full border-2 border-emerald-400/25 border-t-emerald-400 animate-spin"
          data-testid="design-stage-loader"
        />
      )}
      <div
        className="relative [container-type:size]"
        style={{
          width: h ? `min(100vw, ${Math.round(h * 0.5625)}px)` : "min(100vw, calc(100dvh * 0.5625))",
          height: h ? `min(${Math.round(h)}px, calc(100vw * 1.7777778))` : "min(100dvh, calc(100vw * 1.7777778))",
          transform: shift ? `translateY(-${shift}px)` : undefined,
          opacity: artReady ? 1 : 0,
          transition: "transform 200ms ease-out, opacity 240ms ease-out",
        }}
        data-keyboard-shift={shift}
        data-art-ready={artReady ? "1" : "0"}
        data-testid="design-stage-art"
      >
        <img
          ref={onImg}
          src={bg}
          alt=""
          decoding="sync"
          fetchPriority="high"
          onLoad={() => setArtReady(true)}
          className="absolute inset-0 w-full h-full select-none pointer-events-none"
          style={{ objectFit: "fill" }}
          draggable={false}
        />
        {artReady ? children : null}
      </div>
    </div>
  );
};

/** Positions a child on the artwork. All values are % of the 720x1280 design. */
export const At = ({ l, t, w, h, className = "", children, ...rest }) => (
  <div
    className={`absolute ${className}`}
    style={{ left: `${l}%`, top: `${t}%`, width: `${w}%`, height: `${h}%` }}
    {...rest}
  >
    {children}
  </div>
);
